import axios from 'axios';
import FileUtils from '../utils/file.utils';

const adminUrl = process.env.VUE_APP_ADMIN_URL;

// Solo se exponen el código y el mínimo calculado (no el mensaje crudo), para que el
// componente arme su propio texto y señale el campo que hay que corregir.
const servicePlanSaveError = error => ({
  status: 'error',
  code: error.response?.data?.code,
  requiredMinimum: error.response?.data?.requiredMinimum
});

// El backend responde los errores de la plantilla con un código en `message`
// (p. ej. CONTRACT_TEMPLATE_INVALID_PDF); se expone para que el componente lo traduzca.
// `details` trae los datos del conflicto cuando la plantilla ya existe
// (CONTRACT_TEMPLATE_EXISTS: nombre del archivo y planes que lo usan).
const planSaveError = error => ({
  status: 'error',
  message: error.response?.data?.message,
  details: error.response?.data?.data
});

// El archivo seleccionado viaja como base64 + nombre original; el `File` y el alias
// legacy `contractURL` nunca se envían. La decisión ante un nombre repetido
// (`contractTemplateOnConflict`) solo tiene sentido si viaja un archivo.
const buildPlanPayload = async plan => {
  const { contractTemplateFile, contractTemplateOnConflict, ...payload } = plan;
  delete payload.contractURL;
  if (contractTemplateFile) {
    payload.contractTemplateBase64 = await FileUtils.getBase64(contractTemplateFile);
    payload.contractTemplateFileName = contractTemplateFile.name;
    if (contractTemplateOnConflict) payload.contractTemplateOnConflict = contractTemplateOnConflict;
  }
  return payload;
};

export default class PlansService {
  static async getPlans(page, limit, search, visible, sortBy, sortDesc, countryId, currency) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const params = new URLSearchParams({ page, limit });
      if (search) params.set('search', search);
      if (visible === true) params.set('visible', 'true');
      if (visible === false) params.set('visible', 'false');
      if (sortBy) params.set('sortBy', sortBy);
      if (sortDesc) params.set('sortDesc', 'true');
      if (countryId) params.set('countryId', countryId);
      if (currency) params.set('currency', currency);
      const response = await axios.get(`${adminUrl}/plans?${params.toString()}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async getPlanByCompanyId(companyId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.get(
        `${adminUrl}/company/plans?id=${companyId}&visible=true&page=1&limit=100`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      return { status: 'error' };
    }
  }

  static async getB2CPlanToAssociate(id) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.get(`${adminUrl}/plans/b2c/plans/availables?countryId=${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      return { status: 'error' };
    }
  }

  static async getPlanDetails(planId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.get(`${adminUrl}/plans/${planId}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async createPlan(plan, iso = 'CL') {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const payload = await buildPlanPayload(plan);
      const response = await axios.post(`${adminUrl}/plans?iso=${iso}&flow=false`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return planSaveError(error);
    }
  }

  static async updatePlan(id, data) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const payload = await buildPlanPayload(data);
      const response = await axios.put(`${adminUrl}/plans/${id}`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return planSaveError(error);
    }
  }

  static async deletePlan(id) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.delete(`${adminUrl}/plans/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async getServicesByPlan(planId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.get(
        `${adminUrl}/services?plan=${planId}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async getPlanFeatures(planId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.get(`${adminUrl}/plans/${planId}/features`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.plan.features };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      console.log(error);
      return { error };
    }
  }

  static async addServiceToPlan(data) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.post(`${adminUrl}/services-plans`, data, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      // No se muestra el mensaje crudo en la UI (ver CLAUDE.md), pero se loguea
      // para poder diagnosticar sin adivinar (ej. 400 de validación silencioso).
      console.error('[PlansService.addServiceToPlan]', error.response?.data || error);
      return servicePlanSaveError(error);
    }
  }

  static async updateServicePlan(data) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.put(`${adminUrl}/services-plans/${data.id}`, data, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      console.error('[PlansService.updateServicePlan]', error.response?.data || error);
      return servicePlanSaveError(error);
    }
  }

  static async removeServiceFromPlan(servicePlanId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.delete(`${adminUrl}/services-plans/${servicePlanId}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async addBenefitToPlan(data) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.post(`${adminUrl}/plans/benefits`, data, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async removeBenefitFromPlan(planId, benefitId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.delete(`${adminUrl}/plans/${planId}/benefits/${benefitId}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async addFeatureToPlan(data) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.post(`${adminUrl}/plans/features`, data, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async updateFeaturePlan(featureId, data) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.put(
        `${adminUrl}/plans/features/${featureId}`,
        data,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async getPlansByUserId(userId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.get(`${adminUrl}/plans/availables/${userId}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      return { status: 'error' };
    }
  }

  static async getPlanAllianceExclusions(planId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.get(`${adminUrl}/plans/${planId}/alliance-exclusions`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async addPlanAllianceExclusion(planId, allianceId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.post(
        `${adminUrl}/plans/${planId}/alliance-exclusions`,
        { allianceId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async removePlanAllianceExclusion(planId, allianceId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.delete(
        `${adminUrl}/plans/${planId}/alliance-exclusions/${allianceId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.data.status === 'ok') {
        return { status: 'ok' };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }
}
